import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CategoryIcon from '@mui/icons-material/Category';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import TuneIcon from '@mui/icons-material/Tune';

import { useCalculatorStore } from '../store/calculatorStore';
import { useCalculator } from '../hooks/useCalculator';
import { useConfig, useExport } from '../hooks/useConfig';
import { buildFilename } from '../utils/configFile';
import { exportPagePDF } from '../utils/pdfExport';
import { CostPieChart, type BreakdownView } from './charts/CostPieChart';
// import { ScalingChart } from './charts/ScalingChart'; // Scaling Comparison hidden for now
import { CalculatorRenderer } from './renderers/CalculatorRenderer';
import { KeyMetricsBar } from './renderers/KeyMetricsBar';
import { CostKeyContributors } from './renderers/CostKeyContributors';
import { InputsSummary } from './renderers/InputsSummary';

export function CalculatorView() {
  const schema = useCalculatorStore((s) => s.schema);
  const params = useCalculatorStore((s) => s.params);
  const breakdown = useCalculatorStore((s) => s.breakdown);
  const configName = useCalculatorStore((s) => s.configName);
  const resetToQuestionnaire = useCalculatorStore((s) => s.resetToQuestionnaire);
  const sidebarOpen = useCalculatorStore((s) => s.sidebarOpen);
  const toggleSidebar = useCalculatorStore((s) => s.toggleSidebar);

  const { calculateAll, error: calcError } = useCalculator();
  const { saveToFile, error: saveError } = useConfig();
  const { exportCSV, loading: exportLoading } = useExport();

  // Name-prompt dialog. `pendingAction` records what to do once the user
  // provides a name. All three actions share the same prompt; with a name
  // already set, they're effectively single-click.
  type PendingAction = 'save' | 'csv' | 'pdf';
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>('save');
  const [draftName, setDraftName] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);

  const [breakdownView, setBreakdownView] = useState<BreakdownView>('service');

  // Debounced recalc on param change. Depends on calculateAll identity
  // being stable (see useCalculator's useCallback deps).
  useEffect(() => {
    const t = setTimeout(() => {
      calculateAll();
    }, 300);
    return () => clearTimeout(t);
  }, [params, calculateAll]);

  const runAction = async (action: PendingAction, name: string) => {
    if (action === 'save') {
      saveToFile(name);
      return;
    }
    if (action === 'csv') {
      exportCSV(name);
      return;
    }
    // PDF: capture the live DOM and pack it into a multi-page A4 PDF.
    // No print dialog. Layout mirrors the on-screen page via print.css
    // rules triggered by the `capturing` body class.
    const state = useCalculatorStore.getState();
    const projectLabel =
      state.schema?.project.name ?? state.projectId ?? 'project';
    setPdfBusy(true);
    try {
      await exportPagePDF({
        filename: buildFilename(name, projectLabel, 'pdf'),
      });
    } finally {
      setPdfBusy(false);
    }
  };

  const requireName = (action: PendingAction) => {
    if (configName) {
      runAction(action, configName);
      return;
    }
    setPendingAction(action);
    setDraftName('');
    setNamePromptOpen(true);
  };

  const handleNameSubmit = () => {
    const name = draftName.trim();
    if (!name) return;
    // Save is the only action that needs us to commit a filename via the
    // store. For CSV, we pass the name explicitly. For print, bump configName
    // first so the print header shows it.
    if (pendingAction !== 'save') {
      useCalculatorStore.getState().setConfigName(name);
    }
    setNamePromptOpen(false);
    runAction(pendingAction, name);
  };

  const handleSave = () => requireName('save');
  const handleExportCsv = () => requireName('csv');
  const handlePrint = () => requireName('pdf');

  if (!schema) return null;

  const serviceItems = breakdown
    ? Object.entries(breakdown.service_totals).map(([id, cost]) => ({
        service_id: id,
        service_name: schema.service_display_names[id] ?? id,
        monthly_cost: cost,
        percentage:
          breakdown.total_monthly > 0
            ? (cost / breakdown.total_monthly) * 100
            : 0,
        cost_drivers: [] as string[],
        details: {},
      }))
    : [];

  return (
    <Box>
        {/* Toolbar */}
        <Box
          className="no-print"
          sx={{
            display: 'flex',
            gap: 1.5,
            mb: 3,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant={sidebarOpen ? 'contained' : 'outlined'}
            startIcon={<TuneIcon />}
            onClick={toggleSidebar}
            size="small"
          >
            {sidebarOpen ? 'Hide Inputs' : 'Show Inputs'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={resetToQuestionnaire}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exportLoading}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handlePrint}
            disabled={pdfBusy}
          >
            {pdfBusy ? 'Generating PDF…' : 'Export PDF'}
          </Button>
        </Box>

      {/* Cost Key Contributors + KeyMetrics */}
      {calcError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {calcError}
        </Alert>
      )}

      {breakdown && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cost Key Contributors</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CostKeyContributors
              breakdown={breakdown}
              classification={schema.cost_classification}
              serviceDisplayNames={schema.service_display_names}
            />
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Key Metrics
              </Typography>
              <KeyMetricsBar
                metrics={schema.key_metrics}
                breakdown={breakdown}
                params={params}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Cost Breakdown accordion */}
      {breakdown && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cost Breakdown</Typography>
            <Chip
              label={
                breakdownView === 'service'
                  ? `${Object.keys(breakdown.service_totals).length} services`
                  : `${breakdown.configs.reduce(
                      (n, c) => n + (c.workflows?.length ?? 0),
                      0
                    )} workflows`
              }
              size="small"
              sx={{ ml: 2 }}
            />
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <ToggleButtonGroup
                value={breakdownView}
                exclusive
                onChange={(_, v) => v && setBreakdownView(v)}
                size="small"
              >
                <ToggleButton value="service">
                  <CategoryIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography variant="caption">By Service</Typography>
                </ToggleButton>
                <ToggleButton value="workflow">
                  <AccountTreeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography variant="caption">By Workflow</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 300 }}>
                <CostPieChart
                  services={serviceItems}
                  usageConfigs={breakdown.configs}
                  view={breakdownView}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {breakdownView === 'service'
                    ? 'Service Breakdown'
                    : 'Workflow Breakdown'}
                </Typography>

                {breakdownView === 'service'
                  ? serviceItems.map((service) => (
                      <Box
                        key={service.service_id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2">
                          {service.service_name}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" fontWeight={500}>
                            ${service.monthly_cost.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {service.percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  : breakdown.configs.map((config) => (
                      <Box key={config.config_name} sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 1,
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                            px: 1,
                            borderRadius: '4px 4px 0 0',
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {config.config_label || config.config_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary.main"
                          >
                            ${config.config_cost.toFixed(2)}
                          </Typography>
                        </Box>
                        {config.workflows
                          ?.filter((w) => w.total_cost > 0)
                          .map((workflow) => (
                            <Box
                              key={workflow.name}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                py: 0.75,
                                pl: 2,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                              }}
                            >
                              <Box>
                                <Typography variant="body2">
                                  {workflow.label}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {workflow.description}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                                <Typography variant="body2" fontWeight={500}>
                                  ${workflow.total_cost.toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Workflow sections from schema — report view only; sliders live in sidebar. */}
      <CalculatorRenderer
        schema={schema}
        params={params}
        breakdown={breakdown}
      />

      {/* Scaling Comparison hidden for now.
      <Accordion
        defaultExpanded
        sx={{ mt: 3 }}
        data-pdf-page-break-before="true"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Scaling Comparison</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ScalingChart
            axis={schema.scaling.axis}
            servicesToPlot={schema.scaling.services_to_plot}
            currentValue={Number(params[schema.scaling.axis] ?? 0)}
          />
        </AccordionDetails>
      </Accordion>
      */}

      {/* Print-only appendix listing every parameter used. Hidden on
          screen; shown in PDF/print so the output is self-referential. */}
      <InputsSummary schema={schema} params={params} />

      {/* Name-required dialog. Reused across Save / Export CSV / Export PDF. */}
      <Dialog
        open={namePromptOpen}
        onClose={() => setNamePromptOpen(false)}
        className="no-print"
      >
        <DialogTitle>
          {pendingAction === 'save'
            ? 'Save Configuration'
            : 'Name this estimate'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {pendingAction === 'save'
              ? 'Pick a name — a .json file will download to your computer. '
                + 'Upload it from the home page to resume where you left off.'
              : 'Give this estimate a name before exporting. You can rename '
                + 'it any time from the header pill.'}
          </Typography>
          <TextField
            autoFocus
            label="Name"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draftName.trim()) handleNameSubmit();
            }}
            fullWidth
            placeholder="production-estimate-v1"
          />
          {saveError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNamePromptOpen(false)}>Cancel</Button>
          <Button
            onClick={handleNameSubmit}
            variant="contained"
            disabled={!draftName.trim()}
          >
            {pendingAction === 'save'
              ? 'Download'
              : pendingAction === 'csv'
              ? 'Export CSV'
              : 'Export PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
